import { useEffect, useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import SlideOver from './SlideOver'
import { ExternalLink, Save, X, Edit, Loader2, AlertCircle, DollarSign } from 'lucide-react'

interface CreditData {
  id: number
  agent_id: number
  agent_name: string
  credit_amount: number
  credit_type: string
  event_name: string
  event_date: string
  notes: string | null
  source_type: string | null
  source_id: number | null
  granted_by_id: number | null
  granted_by_name: string | null
  created_at: string
}

interface SelectOption { id: number; name: string }

interface Props {
  creditId: number | null
  isOpen: boolean
  onClose: () => void
  onSaved?: () => void
}

const typeColors: Record<string, string> = {
  production: 'bg-green-100 text-green-700',
  override: 'bg-blue-100 text-blue-700',
  bonus: 'bg-purple-100 text-purple-700',
  adjustment: 'bg-orange-100 text-orange-700'
}

export default function EquityCreditDetailPanel({ creditId, isOpen, onClose, onSaved }: Props) {
  const { auth, ...pageProps } = usePage<PageProps>().props
  const isAdmin = auth.user?.role === 'owner' || auth.user?.role === 'administrator'
  const csrfFromProps = (pageProps as { csrf_token?: string }).csrf_token

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [credit, setCredit] = useState<CreditData | null>(null)
  const [agents, setAgents] = useState<SelectOption[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<{
    agent_id: number
    credit_amount: number
    credit_type: string
    event_name: string
    event_date: string
    notes: string
  } | null>(null)

  useEffect(() => {
    if (!isOpen || !creditId) return
    setLoading(true)
    setError(null)
    setIsEditing(false)
    setCredit(null)

    fetch(`/equity_credits/${creditId}`, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load equity credit (${res.status})`)
        return res.json()
      })
      .then((data) => {
        setCredit(data.credit)
        setAgents(data.agents || [])
        setForm({
          agent_id: data.credit.agent_id,
          credit_amount: data.credit.credit_amount,
          credit_type: data.credit.credit_type,
          event_name: data.credit.event_name,
          event_date: data.credit.event_date,
          notes: data.credit.notes || '',
        })
      })
      .catch((err) => setError(err.message || 'Failed to load credit'))
      .finally(() => setLoading(false))
  }, [creditId, isOpen])

  const csrfToken = () =>
    csrfFromProps ||
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ||
    ''

  const handleSave = async () => {
    if (!credit || !form) return
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`/equity_credits/${credit.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ equity_credit: form }),
      })
      if (res.status === 422) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body.errors || []).join(', ') || 'Save failed')
      }
      if (!res.ok) {
        throw new Error(`Save failed (${res.status})`)
      }
      const refreshed = await fetch(`/equity_credits/${credit.id}`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json())
      setCredit(refreshed.credit)
      setIsEditing(false)
      if (onSaved) onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const title = credit ? `Equity Credit: ${credit.event_name}` : 'Equity Credit'

  return (
    <SlideOver isOpen={isOpen} onClose={onClose} title={title}>
      {loading && (
        <div className="flex items-center justify-center py-12 text-chestnut-charcoal/60">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {error && !loading && (
        <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <div className="text-sm">{error}</div>
        </div>
      )}

      {credit && form && !loading && (
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${typeColors[credit.credit_type]}`}>
              {credit.credit_type}
            </span>
            {isAdmin && (
              <div className="flex items-center gap-2">
                {!isEditing ? (
                  <button onClick={() => setIsEditing(true)} className="btn-secondary flex items-center gap-2 text-xs">
                    <Edit className="h-4 w-4" />
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="btn-secondary flex items-center gap-2 text-xs"
                      disabled={saving}
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="btn-primary flex items-center gap-2 text-xs"
                      disabled={saving}
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Save
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="card p-4">
            <label className="block text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wide mb-1">
              Credits Granted
            </label>
            {isEditing ? (
              <input
                type="number"
                step="0.01"
                value={form.credit_amount}
                onChange={(e) => setForm({ ...form, credit_amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
              />
            ) : (
              <p className="text-3xl font-bold text-chestnut-forest">
                +{credit.credit_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            )}
          </div>

          <div className="card p-4 space-y-4">
            <h3 className="font-semibold text-chestnut-charcoal text-sm border-b border-chestnut-charcoal/10 pb-2">
              Credit Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Agent">
                {isEditing ? (
                  <select
                    value={form.agent_id}
                    onChange={(e) => setForm({ ...form, agent_id: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  >
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                ) : (
                  <Link href={`/agents/${credit.agent_id}`} className="text-chestnut-forest hover:underline font-medium">
                    {credit.agent_name}
                  </Link>
                )}
              </Field>

              <Field label="Credit Type">
                {isEditing ? (
                  <select
                    value={form.credit_type}
                    onChange={(e) => setForm({ ...form, credit_type: e.target.value })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  >
                    <option value="production">Production</option>
                    <option value="override">Override</option>
                    <option value="bonus">Bonus</option>
                    <option value="adjustment">Adjustment</option>
                  </select>
                ) : (
                  <span className="capitalize">{credit.credit_type}</span>
                )}
              </Field>

              <Field label="Event Name">
                {isEditing ? (
                  <input
                    type="text"
                    value={form.event_name}
                    onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  />
                ) : (
                  <span>{credit.event_name}</span>
                )}
              </Field>

              <Field label="Event Date">
                {isEditing ? (
                  <input
                    type="date"
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  />
                ) : (
                  <span>{new Date(credit.event_date).toLocaleDateString()}</span>
                )}
              </Field>
            </div>

            <Field label="Notes">
              {isEditing ? (
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                />
              ) : (
                <span className="text-sm">{credit.notes || 'No notes'}</span>
              )}
            </Field>
          </div>

          <div className="card p-4 space-y-3">
            <Field label="Source">
              {credit.source_type === 'Commission' && credit.source_id ? (
                <Link
                  href={`/commissions/${credit.source_id}`}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-chestnut-forest/5 border border-chestnut-forest/20 rounded-lg text-chestnut-forest hover:bg-chestnut-forest/10 text-sm"
                >
                  <DollarSign className="h-4 w-4" />
                  <span className="font-medium">View Commission #{credit.source_id}</span>
                </Link>
              ) : (
                <span className="text-sm">{credit.granted_by_name || 'Manual'}</span>
              )}
            </Field>

            <Field label="Created">
              <span className="text-sm">{new Date(credit.created_at).toLocaleString()}</span>
            </Field>
          </div>

          <div className="pt-2">
            <Link
              href={`/equity_credits/${credit.id}`}
              className="inline-flex items-center gap-2 text-sm text-chestnut-forest hover:underline font-medium"
            >
              <ExternalLink className="h-4 w-4" />
              Open Full Page
            </Link>
          </div>
        </div>
      )}
    </SlideOver>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="block text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wide mb-1">{label}</label>
      <div className="text-chestnut-charcoal">{children}</div>
    </div>
  )
}
